import React, { useEffect, useRef, useState } from 'react';
import { AiAssistantPanel } from './AiAssistantPanel';
// Stable references so default props don't create new objects each render.
const EMPTY_MESSAGES = [];
const EMPTY_CONTEXT = {};
const createMessage = (role, content) => ({
    id: crypto.randomUUID(),
    role,
    content,
    createdAt: new Date().toISOString()
});
export const AiAssistant = ({ name = 'AI Assistant', context = EMPTY_CONTEXT, defaultOpen = false, status, onRetry, initialMessages = EMPTY_MESSAGES, client }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [sessionState, setSessionState] = useState({
        status: 'loading'
    });
    // `status` lets a caller (Storybook) pin the panel to a specific state
    // without going through a real `createSession` call.
    const effectiveStatus = status ?? sessionState.status;
    const sessionId = sessionState.status === 'ready' ? sessionState.sessionId : null;
    const [messages, setMessages] = useState(initialMessages);
    const [isReplying, setIsReplying] = useState(false);
    // Ignores stale createSession()/sendMessage() responses from superseded calls.
    const latestRequestId = useRef(0);
    // The in-flight sendMessage's controller, if any — aborted by startSession
    // so a superseded reply's stream stops consuming network/CPU immediately
    // instead of running to completion just to be discarded.
    const activeSendController = useRef(null);
    // Close on Escape while the panel is open.
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape')
                setIsOpen(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);
    // Reserve space for the panel by reflowing the page content to the left
    // while it's open (see the `body.ai-assistant-page-open` rule in the CSS).
    useEffect(() => {
        document.body.classList.toggle('ai-assistant-page-open', isOpen);
        return () => {
            document.body.classList.remove('ai-assistant-page-open');
        };
    }, [isOpen]);
    // Tells the CSS to shift HubSpot's help widget out of the AI Assistant button's way.
    // TODO: avoid targeting body directly — revisit once AiAssistantWrapper owns more of this.
    useEffect(() => {
        document.body.classList.add('ai-assistant-enabled');
        return () => {
            document.body.classList.remove('ai-assistant-enabled');
        };
    }, []);
    // Starts a brand-new session — nothing is persisted or rehydrated, so every
    // open (or retry) gets a fresh `sessionId` and an empty transcript.
    const startSession = () => {
        const requestId = ++latestRequestId.current;
        const isStale = () => latestRequestId.current !== requestId;
        setSessionState({ status: 'loading' });
        setMessages(EMPTY_MESSAGES);
        // A sendMessage from the previous session may still be in flight; its
        // own .finally() will no-op once requestId is stale, so reset here too,
        // and abort its stream rather than letting it run to completion unread.
        setIsReplying(false);
        activeSendController.current?.abort();
        activeSendController.current = null;
        client
            .createSession()
            .then((response) => {
            if (isStale())
                return;
            setSessionState({ status: 'ready', sessionId: response.sessionId });
        })
            .catch((error) => {
            if (isStale())
                return;
            console.error(error);
            setSessionState({ status: 'error' });
        });
    };
    const handleOpen = () => {
        setIsOpen(true);
        // When `status` is passed, the caller owns the displayed state (Storybook
        // pinning a specific state), so skip the real session lifecycle.
        if (!status)
            startSession();
    };
    // Mirrors what clicking the Fab does — otherwise a panel that starts open
    // via `defaultOpen` would render the `ready` chat UI without ever having
    // called `client.createSession()`.
    useEffect(() => {
        if (defaultOpen)
            handleOpen();
        // Mount-only: `defaultOpen` is documented as an initial-render setting.
    }, []);
    const handleClose = () => {
        setIsOpen(false);
    };
    const handleSend = (text) => {
        setMessages((prev) => [...prev, createMessage('user', text)]);
        // Composer only renders (and thus can only call this) once a session is
        // ready, but sessionId's type doesn't know that.
        if (!sessionId)
            return;
        const requestId = latestRequestId.current;
        const isStale = () => latestRequestId.current !== requestId;
        // Placeholder bubble that grows in place as tokens stream in, then gets
        // swapped for the final message once the reply completes. It's always
        // the last array element — nothing else can append until it settles,
        // since the composer is disabled for the whole `isReplying` window — so
        // updates below replace the last element directly rather than searching
        // the whole array by id.
        const placeholder = createMessage('assistant', '');
        setMessages((prev) => [...prev, placeholder]);
        setIsReplying(true);
        const controller = new AbortController();
        activeSendController.current = controller;
        const onToken = (contentSoFar) => {
            if (isStale())
                return;
            setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { ...placeholder, content: contentSoFar };
                return next;
            });
        };
        client
            .sendMessage({
            sessionId,
            text,
            jobId: context.jobId,
            onToken,
            signal: controller.signal
        })
            .then((reply) => {
            if (isStale())
                return;
            // Keeps the placeholder's id so React doesn't remount the bubble's
            // DOM node on completion (`reply` carries its own, different id).
            setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { ...reply, id: placeholder.id };
                return next;
            });
        })
            .catch((error) => {
            if (isStale())
                return;
            console.error(error);
            // Visible rather than silently discarding the placeholder — the
            // user should know their message didn't get a reply.
            setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                    ...placeholder,
                    content: 'Something went wrong. Please try again.'
                };
                return next;
            });
        })
            .finally(() => {
            // Cleared unconditionally (even if stale) so a completed request's
            // controller isn't held onto after it no longer represents anything
            // in flight; startSession only ever needs to abort the current one.
            if (activeSendController.current === controller) {
                activeSendController.current = null;
            }
            if (isStale())
                return;
            setIsReplying(false);
        });
    };
    // Real usage (no `status` pinned) always wires retry back to a fresh
    // session attempt unless the caller supplies its own `onRetry`.
    const effectiveOnRetry = onRetry ?? (status ? undefined : startSession);
    // Once the streaming placeholder has its first token, its own growing
    // content is the "replying" signal — showing the typing indicator too
    // would be redundant. Computed here (not in MessageList) since this is the
    // component that owns the placeholder-message convention.
    const showTypingIndicator = isReplying && messages.at(-1)?.content === '';
    return (React.createElement(AiAssistantPanel, { context: context, isOpen: isOpen, isReplying: isReplying, messages: messages, name: name, onClose: handleClose, onOpen: handleOpen, onRetry: effectiveOnRetry, onSend: handleSend, sessionId: sessionId, showTypingIndicator: showTypingIndicator, status: effectiveStatus }));
};
